import React, { useMemo } from "react";
import { createSalonAdminAnalyticsClient } from "./salonAdminAnalyticsApi";
import FlowAnalyticsView from "./FlowAnalyticsView";
import PartnerServiceAnalytics from "../../partner/pages/PartnerServiceAnalytics";
import PartnerStaffAnalytics from "../../partner/pages/PartnerStaffAnalytics";
import PartnerCustomerAnalytics from "../../partner/pages/PartnerCustomerAnalytics";

function portalMeta(portalId) {
  if (portalId === "workspace") {
    return { base: "/naturals/workspace", label: "HQ workspace", hq: true };
  }
  if (portalId === "executive") {
    return { base: "/naturals/executive", label: "Executive", hq: true };
  }
  return { base: "/naturals/partner", label: "Franchise partner", hq: false };
}

function useAnalyticsClient(portalId) {
  return useMemo(() => createSalonAdminAnalyticsClient(portalId), [portalId]);
}

function hqApiForPortal(portalId, hqApi) {
  const { hq } = portalMeta(portalId);
  return hq ? hqApi : null;
}

export function SkinFunnelAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <FlowAnalyticsView
      portalLabel={label}
      title="Skin analysis · funnel"
      subtitle="Skin analysis sessions, completions, and recommendation funnel (services & products)."
      portalBase={base}
      lockedFlowType="skin_analysis"
      hideFlowChips
      fetchFlowAnalytics={client.fetchFlowAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function HairFunnelAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <FlowAnalyticsView
      portalLabel={label}
      title="Hair analysis · funnel"
      subtitle="Hair analysis sessions, completions, and recommendation funnel (services & products)."
      portalBase={base}
      lockedFlowType="hair_analysis"
      hideFlowChips
      fetchFlowAnalytics={client.fetchFlowAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function SkinServiceAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <PartnerServiceAnalytics
      portalBase={base}
      portalLabel={label}
      title="Skin analysis · service KPIs"
      subtitle="Service catalog and taken lines for visits (includes analysis-sourced services)."
      fetchServiceAnalytics={client.fetchServiceAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function HairServiceAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <PartnerServiceAnalytics
      portalBase={base}
      portalLabel={label}
      title="Hair analysis · service KPIs"
      subtitle="Service performance for hair journeys and desk upsell (same service catalog scope)."
      fetchServiceAnalytics={client.fetchServiceAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function PortalFlowAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <FlowAnalyticsView
      portalLabel={label}
      title="Flow analytics"
      portalBase={base}
      fetchFlowAnalytics={client.fetchFlowAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function PortalStaffAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  return (
    <PartnerStaffAnalytics
      portalBase={base}
      portalLabel={label}
      fetchStaffAnalytics={client.fetchStaffAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}

export function PortalCustomerAnalyticsPage({ portalId = "partner", hqApi = null }) {
  const { base, label, hq } = portalMeta(portalId);
  const client = useAnalyticsClient(portalId);
  const subtitle = hq
    ? "Guest segments and activity across the network — filter by franchise to review outlet performance."
    : "Guest segments, repeat visits, sales, and check-in trends — scoped to your outlet.";
  return (
    <PartnerCustomerAnalytics
      portalBase={base}
      portalLabel={label}
      subtitle={subtitle}
      fetchCustomerAnalytics={client.fetchCustomerAnalytics}
      hqApi={hqApiForPortal(portalId, hqApi)}
    />
  );
}
